<template>
  <ErrorModal />
  <DxScrollView width="100%" class="add-user-form">
    <div class="px-3 pt-3 pr-3">
      <div style="display: flex; justify-content: space-between">
        <h2 class="title bold-font">
          {{ t("account_management.new_user") }}
        </h2>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.salutation") }}
        </div>
        <div class="dx-field-value">
          <DxSelectBox
            v-model:value="newUser.person.salutation"
            :items="salutations"
            :height="35"
            :input-attr="{
              'aria-label': t('account_management.salutation'),
            }"
            :placeholder="t('common.select_placeholder')"
            :hover-state-enabled="false"
            displayExpr="description"
            valueExpr="id"
          />
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.first_name") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="newUser.person.firstName"
            :input-attr="{
              'aria-label': t('account_management.first_name'),
            }"
            :placeholder="t('account_management.first_name')"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'addUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("profile.last_name") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="newUser.person.lastName"
            :input-attr="{
              'aria-label': t('profile.last_name'),
            }"
            :placeholder="t('profile.last_name')"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'addUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">{{ t("account_management.e_mail") }}</div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="newUser.email"
            :input-attr="{ 'aria-label': t('account_management.e_mail') }"
            :placeholder="t('account_management.e_mail')"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'addUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
              <DxEmailRule :message="t('common.invalid_email')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.department") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="newUser.person.department"
            :input-attr="{
              'aria-label': t('account_management.department'),
            }"
            :placeholder="t('account_management.department')"
            @focusOut="focusOutFn()"
          >
          </DxTextBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.start_date") }}
        </div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="newUser.person.startDate"
            :input-attr="{ 'aria-label': t('account_management.start_date') }"
            :placeholder="t('account_management.start_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>

      <div class="dx-field" v-if="isSuperAdmin">
        <div class="dx-field-label">
          {{ t("account_management.account_manager") }}
        </div>
        <div class="dx-field-value" style="display: flex">
          <DxCheckBox
            v-model:value="newUser.person.isAccountManager"
            :placeholder="t('account_management.account_manager')"
            :input-attr="{
              'aria-label': t('account_management.account_manager'),
            }"
            :hint="t('account_management.account_manager')"
          />
        </div>
      </div>
      <div class="dx-field" v-if="customerHasSas">
        <div class="dx-field-label">
          {{ t("account_management.assign_sas_role") }}
        </div>
        <div class="dx-field-value" style="display: flex">
          <DxCheckBox
            v-model:value="newUser.assignSasRole"
            :placeholder="t('account_management.assign_sas_role')"
            :input-attr="{
              'aria-label': t('account_management.assign_sas_role'),
            }"
            :hint="t('account_management.assign_sas_role')"
          />
        </div>
      </div>
      <div class="dx-field" v-show="sasFieldsVisible">
        <div class="dx-field-label">
          {{ t("account_management.receives_sas_task_emails") }}
        </div>
        <div class="dx-field-value" style="display: flex">
          <DxCheckBox
            v-model:value="newUser.person.receivesSasTaskEmails"
            :placeholder="t('account_management.receives_sas_task_emails')"
            :input-attr="{
              'aria-label': t('account_management.receives_sas_task_emails'),
            }"
            :hint="t('account_management.receives_sas_task_emails')"
          >
          </DxCheckBox>
        </div>
      </div>
      <div class="dx-field" v-show="sasFieldsVisible">
        <div class="dx-field-label">
          {{ t("account_management.sas_start_date") }}
        </div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="newUser.person.sasStartDate"
            :input-attr="{
              'aria-label': t('account_management.sas_start_date'),
            }"
            :placeholder="t('account_management.sas_start_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>
      <div class="dx-field" v-if="sasFieldsVisible">
        <div class="dx-field-label">
          {{ t("account_management.sas_role") }}
        </div>
        <div class="dx-field-value">
          <DxSelectBox
            v-model:value="newUser.sasRoleId"
            :items="sasRoles"
            display-expr="name"
            value-expr="id"
            :placeholder="t('common.select_placeholder')"
            :input-attr="{
              'aria-label': t('account_management.sas_role'),
            }"
            @focusOut="focusOutFn()"
          >
            <DxValidator
              :validation-group="'addUserValidationGroup'"
              :validation-rules="[{ reevaluate: true }]"
            >
              <DxCustomRule
                :validation-callback="validateSasRole"
                :message="t('common.required_field')"
              />
            </DxValidator>
          </DxSelectBox>
        </div>
      </div>
      <div class="dx-field" v-show="sasFieldsVisible">
        <div class="dx-field-label">
          {{ firstCategorization.parentName }}
        </div>
        <div class="dx-field-value">
          <DxTagBox
            :items="firstCategorization.categories"
            :search-enabled="true"
            :input-attr="{ 'aria-label': firstCategorization.parentName }"
            :show-selection-controls="true"
            apply-value-mode="instantly"
            displayExpr="name"
            valueExpr="id"
            :select-all-text="t('search_schemes.select_all')"
            :placeholder="t('common.select_placeholder')"
            v-model:value="newUser.categoriesIdsInFirstCategorization"
          />
        </div>
      </div>
      <div class="dx-field" v-show="sasFieldsVisible">
        <div class="dx-field-label">
          {{ secondCategorization.parentName }}
        </div>
        <div class="dx-field-value">
          <DxTagBox
            :items="secondCategorization.categories"
            :search-enabled="true"
            :input-attr="{ 'aria-label': secondCategorization.parentName }"
            :show-selection-controls="true"
            apply-value-mode="instantly"
            displayExpr="name"
            valueExpr="id"
            :select-all-text="t('search_schemes.select_all')"
            :placeholder="t('common.select_placeholder')"
            v-model:value="newUser.categoriesIdsInSecondCategorization"
          />
        </div>
      </div>

      <div
        class="mt-4 btns-row"
        style="display: flex; justify-content: space-evenly"
      >
        <DxButton
          :text="t('common.save')"
          :disabled="isButtonDisabled"
          @click="addNewUser()"
          class="orange-btn-filled"
        />
        <DxButton
          :text="t('common.cancel')"
          class="orange-btn bg-gray-button"
          @click="showWarningModal()"
        />
      </div>
    </div>
    <WarningModal :onOk="handleOk" v-if="warningModalVisible" />
  </DxScrollView>
</template>
<script setup>
import {
  computed,
  reactive,
  watch,
  ref,
  onBeforeUnmount,
  onMounted,
} from "vue";
import ErrorModal from "./ErrorModal.vue";
import WarningModal from "../global-components/WarningModal.vue";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxButton } from "devextreme-vue/button";
import { DxCheckBox } from "devextreme-vue/check-box";
import { DxTagBox } from "devextreme-vue/tag-box";
import { DxDateBox } from "devextreme-vue/date-box";
import {
  DxValidator,
  DxRequiredRule,
  DxEmailRule,
  DxCustomRule,
} from "devextreme-vue/validator";
import validationEngine from "devextreme/ui/validation_engine";
import UserService from "../../services/userprofile.service";
import store from "../../store/index";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const validationGroupName = "addUserValidationGroup";
const isButtonDisabled = ref(false);

onMounted(async () => {
  await UserService.getSasRoles(true);
  await UserService.getSasCategories();
});
const salutations = computed({
  get: () => store.state.user.salutationsList,
});
const isSuperAdmin = computed({
  get: () => store.state.auth.authUser.isSuperAdmin,
});
const customerName = computed({
  get: () => store.state.user.customer.name,
});
const customerId = computed({
  get: () => store.state.user.customer.id,
});
const customerHasSas = computed(() => store.state.auth.authUser.customerHasSas);
const warningModalVisible = computed(
  () => store.state.warningModal.warningModalVisible
);
const firstCategorization = computed(
  () => store.state.user.firstCategorization
);
const secondCategorization = computed(
  () => store.state.user.secondCategorization
);
const sasRoles = computed(() => store.state.user.sasRoles);

const newUser = reactive({
  email: "",
  assignSasRole: false,
  sasRoleId: null,
  categoriesIdsInFirstCategorization: [],
  categoriesIdsInSecondCategorization: [],
  categoriesIds: [],
  person: {
    firstName: "",
    lastName: "",
    salutation: 3,
    department: "",
    startDate: null,
    sasStartDate: null,
    isAccountManager: false,
    receivesSasTaskEmails: false,
    customer: {
      name: customerName,
      value: customerId,
    },
  },
});

const sasFieldsVisible = ref(false);
const isDirty = ref(false);

watch(
  () => newUser,
  () => {
    isDirty.value = true;
  },
  { deep: true }
);

watch(
  () => newUser.assignSasRole,
  (next) => {
    sasFieldsVisible.value = next;
  }
);

const showWarningModal = () => {
  if (isDirty.value) {
    store.commit(
      "warningModal/setWarningModalTitle",
      t("common.unsaved_changes_popup_title")
    );

    store.commit(
      "warningModal/setWarningModalText",
      t("common.warning_message")
    );

    store.commit("warningModal/openWarningModal");

    store.commit(
      "warningModal/setWarningModalTooltipText",
      t("common.warning_yes_tooltip_message"),
      t("common.warning_no_tooltip_message")
    );
  } else {
    closeAddNewUserPopup();
  }
};

const resetAddFields = () => {
  newUser.email = "";
  newUser.person.firstName = "";
  newUser.person.lastName = "";
  newUser.person.salutation = 3;
  newUser.person.isAccountManager = false;
  newUser.person.department = "";
  newUser.person.startDate = null;
  newUser.person.sasStartDate = null;
  newUser.assignSasRole = false;
  newUser.sasRoleId = null;
  newUser.categoriesIdsInFirstCategorization.length = 0;
  newUser.categoriesIdsInSecondCategorization.length = 0;
  newUser.categoriesIds.length = 0;
  newUser.person.receivesSasTaskEmails = false;
};

const closeAddNewUserPopup = () => {
  resetAddFields();
  store.commit("user/disableAddUser");
  window.removeEventListener("beforeunload", handleBeforeUnload);
};

const handleOk = () => {
  store.commit("warningModal/closeWarningModal");
  closeAddNewUserPopup();
};

const handleBeforeUnload = (event) => {
  if (isDirty.value) {
    event.preventDefault();
    event.returnValue = "";
  }
};

window.addEventListener("beforeunload", handleBeforeUnload);

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

const focusOutFn = () => {
  validateForm();
};

const validateSasRole = ({ value }) => {
  return !(!value && sasFieldsVisible.value === true);
};

const validateForm = () => {
  const result = validationEngine.validateGroup(validationGroupName);
  if (result) {
    isButtonDisabled.value = !result.isValid;
  } else {
    isButtonDisabled.value = true;
  }
  return result?.isValid || false;
};

const showReactivateSoftDeletedUserModal = computed({
  get: () => store.state.user.showReactivateSoftDeletedUserModal,
});

const addNewUser = async () => {
  await UserService.checkIfSoftDeletedUserExists(
    newUser.email,
    newUser.person.customer.value
  )
    .then(async (response) => {
      if (response.status === 200) {
        if (showReactivateSoftDeletedUserModal.value) {
          store.commit(
            "user/updateModalError",
            t("account_management.soft_deleted_user_exists")
          );
          store.commit("user/enableModalError");
          closeAddNewUserPopup();
          return;
        }
      }
    })
    .catch((error) => {
      store.commit("user/updateModalError", error.response.data);
      store.commit("user/enableModalError");
      closeAddNewUserPopup();
    });

  let isValidForm = validateForm();
  if (isValidForm) {
    newUser.categoriesIds = newUser.categoriesIdsInFirstCategorization.concat(
      newUser.categoriesIdsInSecondCategorization
    );
    await UserService.addNewUser(newUser, locale)
      .then((response) => {
        if (response.status === 200) {
          store.commit("user/triggerUsersGridRefresh");
          store.commit("infoModal/openInfoModal");
          store.commit(
            "infoModal/setInfoModalText",
            t("account_management.activation_invitation_has_been_sent")
          );
        }
      })
      .catch((error) => {
        if (error) {
          store.commit("user/updateModalError", error.response.data);
          store.commit("user/enableModalError");
        }
      });

    closeAddNewUserPopup();
  }
};
</script>
<style scoped lang="scss">
.add-user-form {
  width: 600px !important;
  background-color: white;

  .title {
    color: #0a108f;
    font-size: 18px;
    margin-left: 10px;
  }
  .dx-overlay-content {
    height: 94vh !important;
  }

  .dx-field {
    margin: 10px !important;

    .dx-field-label {
      font-family: "ProximaNovaBold";
      color: #0a108f;
      text-align: left;
    }
  }

  .dx-field {
    width: 80%;
    margin-left: 3% !important;
  }
  .btns-row {
    width: 50%;
    margin-left: 18%;
  }
}

@media screen and (max-width: 1028px) {
  .add-user-form {
    width: 300px !important;
  }
  .btns-row {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
}
</style>
